import React, { forwardRef, useEffect, useImperativeHandle, useMemo, useRef, useState } from "react";
import { Box, CircularProgress, IconButton, Typography } from "@mui/material";
import FullscreenIcon from "@mui/icons-material/Fullscreen";
import DownloadIcon from "@mui/icons-material/Download";
import ReactDOMServer from "react-dom/server";

import "./evidenceImage.css";
import { useAppSelector } from "../../redux";
import { convertToTitleCase, downloadImage } from "../../utils/commonUtils";
import { BBOX_STROKE_COLOR, BBOX_STROKE_WIDTH, VEHICLE_TYPES_LIST } from "../../utils/constants";
import { useGetSiteConfigQuery, useGetSocietyConfigQuery } from "../../redux/reducers/settings.reducer";
import { ReactComponent as NoImageIcon } from "../../assets/icons/image-not-found-icon.svg";
import moment from "moment";
import { IVehicleEventDetails } from "../../components/Dashboard";
import { IFullscreenImageProps } from "../../components/Dashboard/LogDetailsDialog";
import { ICoordinate, IDimensions } from "../CameraSetup/interfaces";
import { getScaledCoordinate } from "../CameraSetup/utils";
import { useLazyGetEventFrameQuery } from "../../redux/reducers/dashboard.reducer";

interface IEvidenceImageProps {
	vehicleLogDetails: IVehicleEventDetails | null;
	bbox: [number, number, number, number][];
	onOpenFullscreenImage: (props: IFullscreenImageProps) => void;
}

export interface IEvidenceImageRef {
	downloadImage: () => void;
}

export interface ICarouselOverlayDetails {
	productName: string;
	timestamp: string;
	vehicleType: string;
	numberPlate: string;
	clientName: string;
	auth: string;
	location: string;
}

interface ICarouselItemProps {
	src: string;
	frameID: string;
	bbox: [number, number, number, number][];
	imageDimensions: IDimensions;
	overlayDetails: ICarouselOverlayDetails;
	width: number;
	handleShowFullscreenImage: (src: string, overlayDetails: ICarouselOverlayDetails) => void;
}

interface ICarouselItemRef {
	downloadImage: () => void;
}

interface IDownloadImageDetails {
	src: string;
	width: number;
	height: number;
}

// const CarouselThumb: FC<{ src: string }> = ({ src }) => {
// 	const [showError, setShowError] = useState<boolean>(false);

// 	if (showError) {
// 		return (
// 			<Box sx={{ width: "100%", height: "100%", display: "flex", justifyContent: "center", alignItems: "center" }}>
// 				<NoImageIcon width={34} fill="#d5d5d5" stroke="#d5d5d5" />
// 			</Box>
// 		);
// 	}

// 	return <img src={src} alt={src} onError={() => setShowError(true)} />;
// };

const canvasTopOverlayContent = `<div class="evidence-image-overlay top">
	<div class="evidence-image-overlay-header-wrapper">
		<div class="evidence-image-overlay-header">
			<h2>{product_name}</h2>
			<div class="evidence-image-overlay-header-divider" />
			<h3>{client_name}</h3>
		</div>

		<div class="evidence-image-overlay-timestamp-wrapper">
			<h4>Date: {date}</h4>
			<div class="evidence-image-overlay-header-divider" />
			<h4>Time: {time}</h4>
		</div>
	</div>
</div>`;

const canvasBottomOverlayContent = `<div class="evidence-image-overlay bottom">
	<div class="evidence-image-overlay-content">
		<div class="evidence-image-overlay-content-item" style="flex-shrink: 0">
			<h5 style="white-space: nowrap">
				<span class="evidence-image-overlay-content-name">Plate Number: </span>
				{number_plate}
			</h5>
		</div>

		<div class="evidence-image-overlay-content-item">
			<h5>
				<span class="evidence-image-overlay-content-name">Vehicle Type: </span>
				{vehicle_type}
			</h5>
		</div>

		<div class="evidence-image-overlay-content-item">
			<h5>
				<span class="evidence-image-overlay-content-name">Location: </span>
				{location}
			</h5>
		</div>

		<div class="evidence-image-overlay-content-item">
			<h5>
				<span class="evidence-image-overlay-content-name">Type: </span>
				{auth}
			</h5>
		</div>
	</div>
</div>`;

const canvasOverlayContent = `
	${canvasTopOverlayContent}

	<div style="width: 100%; height: {image_height}px; position: relative">
		{image_content}
	</div>

	${canvasBottomOverlayContent}
`;

const canvasOverlaySVG = `<svg xmlns="http://www.w3.org/2000/svg" width="{canvas_width}" height="{canvas_height}">
	<style type="text/css">
		:root {
			font-size: {font_size}px;
			font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Oxygen', 'Ubuntu', 'Cantarell',
				'Fira Sans', 'Droid Sans', 'Helvetica Neue', sans-serif;
		}
	
		.evidence-image-overlay-wrapper {
			position: fixed;
			top: 0;
			bottom: 0;
			left: 0;
			right: 0;
			font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Oxygen', 'Ubuntu', 'Cantarell',
				'Fira Sans', 'Droid Sans', 'Helvetica Neue', sans-serif;
		}
	
		.evidence-image-overlay {
			background-color: black;
			padding: 1em 1.5em;
			display: flex;
			align-items: center;
			justify-content: space-between;
			gap: 2em;
		}

		.evidence-image-overlay.bottom {
		}

		.evidence-image-overlay.top {
		}

		.evidence-image-overlay-header-wrapper {
			display: flex;
			align-items: center;
			justify-content: space-between;
			width: 100%;
		}

		.evidence-image-overlay-header {
			display: flex;
			align-items: center;
			gap: 1em;
		}

		.evidence-image-overlay-header h2 {
			color: white;
			margin: 0;
		}

		.evidence-image-overlay-header h3 {
			color: white;
			margin: 0.25em 0 0;
		}

		.evidence-image-overlay-header-divider {
			width: 0.125em;
			height: 2.375em;
			background-color: white;
		}

		.evidence-image-overlay-timestamp-wrapper {
			display: flex;
			align-items: center;
			gap: 1em;
		}

		.evidence-image-overlay-timestamp-wrapper h4 {
			color: white;
			margin: 0;
		}

		.evidence-image-overlay-content {
			display: flex;
			align-items: stretch;
			width: 100%;
		}

		.evidence-image-overlay-content-item {
			flex-grow: 1;
			border: 0.0625em solid white;
			padding: 0.25em;
			display: flex;
			align-items: center;
			justify-content: center;
		}

		.evidence-image-overlay-content-item h5 {
			color: white;
			margin: 0;
			font-weight: 500;
			text-align: center;
		}

		.evidence-image-overlay-content-name {
			font-weight: 800;
		}
	</style>
	<foreignObject width="100%" height="100%">
		<div xmlns="http://www.w3.org/1999/xhtml">
			${canvasOverlayContent}
		</div>
	</foreignObject>
</svg>`;

const CarouselItem = forwardRef<ICarouselItemRef, ICarouselItemProps>((props, ref) => {
	const { src, frameID, bbox, imageDimensions, overlayDetails, width, handleShowFullscreenImage } = props;

	const canvasRef = useRef<HTMLCanvasElement>(null);
	const downloadCanvasRef = useRef<HTMLCanvasElement>(null);

	const [showDownloadButtonLoader, setShowDownloadButtonLoader] = useState<boolean>(false);
	const [canvasDimensions, setCanvasDimensions] = useState<IDimensions>({ width: 0, height: 0 });
	const [showError, setShowError] = useState<boolean>(false);

	const canvasWrapperHeight = useMemo<number>(() => {
		if (canvasDimensions.height > 0) {
			return canvasDimensions.height;
		}

		return (width * imageDimensions.height) / imageDimensions.width;
	}, [canvasDimensions.height, imageDimensions, width]);

	// Calculate font sizes based on canvas width
	const fontSizes = useMemo(() => {
		const baseWidth = 800; // Base width for reference
		const scaleFactor = Math.max(0.5, Math.min(1.5, canvasDimensions.width / baseWidth));

		return {
			productName: Math.max(10, Math.min(20, 16 * scaleFactor)),
			clientName: Math.max(8, Math.min(16, 14 * scaleFactor)),
			timestamp: Math.max(8, Math.min(14, 12 * scaleFactor)),
			content: Math.max(8, Math.min(14, 12 * scaleFactor))
		};
	}, [canvasDimensions.width]);

	function getCanvasOverlayURL(canvasWidth: number, canvasHeight: number, imageDetails: IDownloadImageDetails): string {
		const overlayTop = document.createElement("div");
		overlayTop.style.fontSize = `${(imageDetails.height * 16) / 548}px`;
		overlayTop.innerHTML = canvasTopOverlayContent;
		overlayTop.style.width = `${imageDetails.width}px`;
		overlayTop.style.position = "fixed";
		overlayTop.style.top = "99999";
		overlayTop.style.left = "99999";
		overlayTop.style.opacity = "0";

		document.body.appendChild(overlayTop);

		const overlayBottom = document.createElement("div");
		overlayBottom.style.fontSize = `${(imageDetails.height * 16) / 548}px`;
		overlayBottom.innerHTML = canvasBottomOverlayContent;
		overlayBottom.style.width = `${imageDetails.width}px`;
		overlayBottom.style.position = "fixed";
		overlayBottom.style.top = "99999";
		overlayBottom.style.left = "99999";
		overlayBottom.style.opacity = "0";

		document.body.appendChild(overlayBottom);

		const svg = canvasOverlaySVG
			.replace("{canvas_width}", String(canvasWidth))
			.replace("{canvas_height}", String(canvasHeight + overlayTop.offsetHeight + overlayBottom.offsetHeight))
			.replace("{product_name}", overlayDetails.productName)
			.replace("{client_name}", overlayDetails.clientName)
			.replace("{date}", moment(overlayDetails.timestamp).format("YYYY-MM-DD"))
			.replace("{time}", moment(overlayDetails.timestamp).format("hh:mm A"))
			.replace("{number_plate}", overlayDetails.numberPlate)
			.replace("{vehicle_type}", overlayDetails.vehicleType)
			.replace("{location}", overlayDetails.location)
			.replace("{auth}", overlayDetails.auth)
			.replace("{font_size}", String((imageDetails.height * 16) / 548))
			.replace(
				"{image_content}",
				ReactDOMServer.renderToString(
					<img src={imageDetails.src} alt="download" width={imageDetails.width} height={imageDetails.height} />
				)
			);

		document.body.removeChild(overlayTop);
		document.body.removeChild(overlayBottom);

		return `data:image/svg+xml; charset=utf8,${encodeURIComponent(svg)}`;
	}

	function handleDownload() {
		if (!src) return;
		if (!downloadCanvasRef.current) return;

		setShowDownloadButtonLoader(true);

		const canvasContext = downloadCanvasRef.current.getContext("2d");
		if (!canvasContext) return;

		canvasContext.clearRect(0, 0, downloadCanvasRef.current.width, downloadCanvasRef.current.height);

		const backgroundImage = new Image();
		backgroundImage.crossOrigin = "anonymous";

		backgroundImage.onload = () => {
			if (!downloadCanvasRef.current) return;

			downloadCanvasRef.current.width = backgroundImage.width;
			downloadCanvasRef.current.height = backgroundImage.height;

			canvasContext.clearRect(0, 0, downloadCanvasRef.current.width, downloadCanvasRef.current.height);

			canvasContext.drawImage(backgroundImage, 0, 0, downloadCanvasRef.current.width, downloadCanvasRef.current.height);

			canvasContext.strokeStyle = BBOX_STROKE_COLOR;
			canvasContext.lineWidth = BBOX_STROKE_WIDTH;

			const downloadCanvasDimensions: IDimensions = {
				width: downloadCanvasRef.current.width,
				height: downloadCanvasRef.current.height
			};

			if (!src.startsWith("http")) {
				for (const bboxItem of bbox) {
					const startCoordinate: ICoordinate = getScaledCoordinate(
						{ x: bboxItem[0], y: bboxItem[1] },
						imageDimensions,
						downloadCanvasDimensions,
						true
					);
					const bboxDimensions: ICoordinate = getScaledCoordinate(
						{ x: bboxItem[2], y: bboxItem[3] },
						imageDimensions,
						downloadCanvasDimensions,
						true
					);

					canvasContext.strokeRect(startCoordinate.x, startCoordinate.y, bboxDimensions.x, bboxDimensions.y);
				}
			}

			const imageDetails: IDownloadImageDetails = {
				src: downloadCanvasRef.current.toDataURL("image/jpeg"),
				width: backgroundImage.width,
				height: backgroundImage.height
			};

			const svgObjectUrl = getCanvasOverlayURL(
				downloadCanvasRef.current.width,
				downloadCanvasRef.current.height,
				imageDetails
			);

			const canvasImage = new Image();
			canvasImage.crossOrigin = "anonymous";

			canvasImage.onload = () => {
				if (!downloadCanvasRef.current) return;

				let fileName = `${frameID}.jpeg`;

				if (src.startsWith("http")) {
					const splitURL = src.split("/");
					fileName = splitURL[splitURL.length - 1];
				}

				downloadCanvasRef.current.width = canvasImage.width;
				downloadCanvasRef.current.height = canvasImage.height;

				canvasContext.drawImage(canvasImage, 0, 0, downloadCanvasRef.current.width, downloadCanvasRef.current.height);
				downloadImage(downloadCanvasRef.current.toDataURL("image/jpeg"), fileName).finally(() => {
					setShowDownloadButtonLoader(false);
				});

				canvasImage.onload = () => {};
			};

			canvasImage.src = svgObjectUrl;

			backgroundImage.onload = () => {};
		};

		backgroundImage.src = src;
	}

	useEffect(() => {
		if (canvasRef.current && canvasRef.current.offsetWidth) {
			setCanvasDimensions((currentDimensions) => ({
				...currentDimensions,
				width: canvasRef.current?.offsetWidth ?? 0
			}));
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [canvasRef.current, width]);

	useEffect(() => {
		if (canvasDimensions.width && imageDimensions) {
			const canvasHeight = (canvasDimensions.width * imageDimensions.height) / imageDimensions.width;

			setCanvasDimensions((currentDimensions) => ({
				...currentDimensions,
				height: canvasHeight
			}));
		}
	}, [canvasDimensions.width, imageDimensions]);

	useEffect(() => {
		if (canvasRef.current && src && !src.startsWith("http") && canvasDimensions.width && canvasDimensions.height) {
			const canvasContext = canvasRef.current.getContext("2d");
			if (!canvasContext) return;

			canvasContext.clearRect(0, 0, canvasDimensions.width, canvasDimensions.height);

			canvasContext.strokeStyle = BBOX_STROKE_COLOR;
			canvasContext.lineWidth = BBOX_STROKE_WIDTH;

			for (const bboxItem of bbox) {
				const startCoordinate: ICoordinate = getScaledCoordinate(
					{ x: bboxItem[0], y: bboxItem[1] },
					imageDimensions,
					canvasDimensions,
					true
				);
				const bboxDimensions: ICoordinate = getScaledCoordinate(
					{ x: bboxItem[2], y: bboxItem[3] },
					imageDimensions,
					canvasDimensions,
					true
				);

				canvasContext.strokeRect(startCoordinate.x, startCoordinate.y, bboxDimensions.x, bboxDimensions.y);
			}
		}
	}, [bbox, canvasDimensions, imageDimensions, src]);

	useEffect(() => {
		if (src) {
			const srcImage = new Image();
			srcImage.src = src;
			srcImage.onerror = () => setShowError(true);
			srcImage.onload = () => setShowError(false);
		}
	}, [src]);

	useImperativeHandle(ref, () => ({
		downloadImage: handleDownload
	}));

	if (showError) {
		return (
			<Box className="evidence-image-component-wrapper">
				<NoImageIcon width={58} fill="#d5d5d5" stroke="#d5d5d5" />
				<Typography sx={{ color: "#999999", fontSize: { xs: "1rem", sm: "1.25rem" } }}>Image not available</Typography>
			</Box>
		);
	}

	return (
		<Box className="evidence-image-wrapper">
			<Box className="evidence-image-overlay top" sx={{ padding: { xs: "8px 12px", sm: "16px 24px" } }}>
				<Box className="evidence-image-overlay-header-wrapper">
					<Box className="evidence-image-overlay-header">
						<h2 style={{ fontSize: `${fontSizes.productName}px` }}>{overlayDetails.productName}</h2>
						<Box className="evidence-image-overlay-header-divider" />
						<h3 style={{ fontSize: `${fontSizes.clientName}px` }}>{overlayDetails.clientName}</h3>
					</Box>

					<Box className="evidence-image-overlay-timestamp-wrapper">
						<h4 style={{ fontSize: `${fontSizes.timestamp}px` }}>{`Date: ${moment(overlayDetails.timestamp).format(
							"YYYY-MM-DD"
						)}`}</h4>
						<Box className="evidence-image-overlay-header-divider" />
						<h4 style={{ fontSize: `${fontSizes.timestamp}px` }}>{`Time: ${moment(overlayDetails.timestamp).format(
							"hh:mm A"
						)}`}</h4>
					</Box>
				</Box>
			</Box>

			<Box sx={{ width: "100%", height: canvasWrapperHeight, position: "relative" }}>
				<canvas
					ref={canvasRef}
					width={canvasDimensions.width}
					height={canvasDimensions.height}
					style={{
						width: "100%",
						backgroundSize: "contain",
						backgroundPosition: "center",
						backgroundRepeat: "no-repeat",
						backgroundImage: `url(${String(src)})`
					}}
				/>

				<IconButton
					disableRipple
					size="small"
					className="image-action-button right"
					onClick={() => handleShowFullscreenImage(src, overlayDetails)}
					sx={{
						padding: { xs: "4px", sm: "8px" },
						"& .MuiSvgIcon-root": {
							fontSize: { xs: "1.25rem", sm: "1.5rem" }
						}
					}}
				>
					<FullscreenIcon sx={{ color: "white" }} fontSize="large" />
				</IconButton>

				<IconButton
					disableRipple
					size="small"
					className="image-action-button left"
					onClick={handleDownload}
					disabled={showDownloadButtonLoader}
					sx={{
						padding: { xs: "4px", sm: "8px" },
						"& .MuiSvgIcon-root": {
							fontSize: { xs: "1.25rem", sm: "1.5rem" }
						}
					}}
				>
					{showDownloadButtonLoader ? (
						<CircularProgress size={24} />
					) : (
						<DownloadIcon sx={{ color: "white" }} fontSize="large" />
					)}
				</IconButton>
			</Box>

			<Box className="evidence-image-overlay bottom" sx={{ padding: { xs: "8px 12px", sm: "16px 24px" } }}>
				<Box className="evidence-image-overlay-content">
					<Box className="evidence-image-overlay-content-item" sx={{ flexShrink: 0 }}>
						<h5 style={{ whiteSpace: "nowrap", fontSize: `${fontSizes.content}px` }}>
							<span className="evidence-image-overlay-content-name">Plate Number: </span>
							{overlayDetails.numberPlate}
						</h5>
					</Box>

					<Box className="evidence-image-overlay-content-item">
						<h5 style={{ fontSize: `${fontSizes.content}px` }}>
							<span className="evidence-image-overlay-content-name">Vehicle Type: </span>
							{overlayDetails.vehicleType}
						</h5>
					</Box>

					<Box className="evidence-image-overlay-content-item">
						<h5 style={{ fontSize: `${fontSizes.content}px` }}>
							<span className="evidence-image-overlay-content-name">Location: </span>
							{overlayDetails.location}
						</h5>
					</Box>

					<Box className="evidence-image-overlay-content-item">
						<h5 style={{ fontSize: `${fontSizes.content}px` }}>
							<span className="evidence-image-overlay-content-name">Type: </span>
							{overlayDetails.auth}
						</h5>
					</Box>
				</Box>
			</Box>

			<canvas
				ref={downloadCanvasRef}
				style={{
					width: "100%",
					backgroundSize: "contain",
					backgroundPosition: "center",
					backgroundRepeat: "no-repeat",
					backgroundImage: `url(${String(src)})`,
					position: "absolute",
					top: -99999,
					left: -99999,
					opacity: 0
				}}
			/>
		</Box>
	);
});

const EvidenceImage = forwardRef<IEvidenceImageRef, IEvidenceImageProps>((props, ref) => {
	const { bbox, vehicleLogDetails, onOpenFullscreenImage } = props;

	const authState = useAppSelector((state) => state.auth);

	const carouselItemRef = useRef<ICarouselItemRef>(null);

	const [componentWidth, setComponentWidth] = useState<number>(0);

	// APIS
	// GET DEVICE CONFIG
	const { data: siteConfig } = useGetSiteConfigQuery(null, {
		skip: !authState.user.token || authState.user.token === "invalid"
	});

	// GET SOCIETY CONFIG
	const { data: getSocietyConfigResponse } = useGetSocietyConfigQuery(null, { skip: !siteConfig });
	const societyConfig = getSocietyConfigResponse?.results ?? [];

	// GET FRAME IMAGE
	// const [
	// 	getFrameImage,
	// 	{ data: imageData, isLoading: imageLoading, error: getImageError, originalArgs: getEventImageArgs }
	// ] = useGetFrameImageMutation();

	const [
		getFrameImage,
		{
			data: imageData,
			isLoading: imageLoading,
			isFetching: imageFetching,
			error: getImageError,
			originalArgs: getEventImageArgs
		}
	] = useLazyGetEventFrameQuery();

	const imageDimensions = useMemo<IDimensions>(() => {
		if (imageData && imageData.width && imageData.height) {
			return { width: imageData.width, height: imageData.height };
		}

		return { width: 1280, height: 720 };
	}, [imageData]);

	function handleShowFullscreenImage(src: string, overlayDetails: ICarouselOverlayDetails) {
		onOpenFullscreenImage({
			src: src,
			bbox: !src.startsWith("http") && siteConfig && siteConfig.ml_settings.draw_bbox ? bbox : [],
			imageDimensions: imageDimensions,
			overlayDetails: overlayDetails
		});
	}

	function handleDownloadImage() {
		if (carouselItemRef.current) {
			carouselItemRef.current.downloadImage();
		}
	}

	useEffect(() => {
		if (vehicleLogDetails && vehicleLogDetails.id) {
			// getFrameImage({ message_id: vehicleLogDetails.unique_id, image_type: "frame" });
			getFrameImage({ id: vehicleLogDetails.id, data: { frame_type: "image" } });
		}
	}, [getFrameImage, vehicleLogDetails]);

	useImperativeHandle(ref, () => ({
		downloadImage: handleDownloadImage
	}));

	if (imageLoading || imageFetching) {
		return (
			<Box className="evidence-image-component-wrapper">
				<CircularProgress size={24} />
			</Box>
		);
	}

	// if (getImageError || !imageData || (!imageData.image && imageData.evidence_image_urls.length <= 0)) {
	if (getImageError || !imageData || !imageData.frame_data) {
		return (
			<Box className="evidence-image-component-wrapper">
				<NoImageIcon width={58} fill="#d5d5d5" stroke="#d5d5d5" />
				<Typography sx={{ color: "#999999", fontSize: { xs: "1rem", sm: "1.25rem" } }}>Image not available</Typography>
			</Box>
		);
	}

	// return (
	// 	<Carousel
	// 		swipeable
	// 		emulateTouch
	// 		useKeyboardArrows
	// 		showArrows={false}
	// 		showStatus={false}
	// 		showIndicators={false}
	// 		className="evidence-image-carousel"
	// 		renderThumbs={(children) => children.map((item, index) => <CarouselThumb src={String(item)} key={index} />)}
	// 		renderItem={(item) => (
	// 			<CarouselItem
	// 				src={String(item)}
	// 				frameID={vehicleLogDetails?.unique_id ?? ""}
	// 				imageDimensions={imageDimensions}
	// 				overlayDetails={{
	// 					productName: siteConfig?.product_name ?? "GateGuard",
	// 					timestamp: vehicleLogDetails?.timestamp ?? "",
	// 					node: nodesList?.[0]?.device_id ?? "",
	// 					site: "Entry",
	// 					vehicleType: "Car",
	// 					numberPlate: vehicleLogDetails?.vehicle_number ?? "",
	// 					clientName: clientDetails?.[0]?.name ?? ""
	// 				}}
	// 				handleShowFullscreenImage={handleShowFullscreenImage}
	// 				bbox={
	// 					getEventImageArgs?.id === vehicleLogDetails?.id &&
	// 					siteConfig &&
	// 					(siteConfig.ml_settings.draw_vehicle_bbox || siteConfig.ml_settings.draw_number_plate_bbox)
	// 						? bbox
	// 						: []
	// 				}
	// 			/>
	// 		)}
	// 	>
	// 		{/* {[`data:image/png;base64,${imageData.image}`, ...imageData.evidence_image_urls]} */}
	// 		{[`data:image/png;base64,${imageData.frame_data}`]}
	// 	</Carousel>
	// );

	return (
		<Box sx={{ flexGrow: 1, width: 0 }} onLoad={(event) => setComponentWidth(event.currentTarget.offsetWidth ?? 0)}>
			<CarouselItem
				src={`data:image/png;base64,${imageData.frame_data}`}
				frameID={vehicleLogDetails?.unique_id ?? ""}
				imageDimensions={imageDimensions}
				ref={carouselItemRef}
				overlayDetails={{
					productName: siteConfig?.product_name ?? "GateGuard",
					timestamp: vehicleLogDetails?.timestamp ?? "",
					numberPlate: vehicleLogDetails?.vehicle_number ?? "",
					clientName: societyConfig[0]?.name ?? "",
					auth: convertToTitleCase(vehicleLogDetails?.type ?? ""),
					location: vehicleLogDetails?.location ?? "",
					vehicleType:
						VEHICLE_TYPES_LIST.find((vehicleTypeItem) => vehicleTypeItem.id === vehicleLogDetails?.vehicle_type)
							?.name ?? ""
				}}
				handleShowFullscreenImage={handleShowFullscreenImage}
				bbox={
					getEventImageArgs?.id === vehicleLogDetails?.id && siteConfig && siteConfig.ml_settings.draw_bbox ? bbox : []
				}
				width={componentWidth}
			/>
		</Box>
	);
});

export default EvidenceImage;
